/**
 * Resolución perezosa de CFI exacto para un chunk de texto (#12 citas RAG).
 *
 * Los chunks indexados llevan `chapterHref` + `spineIndex` pero NO un CFI
 * intra-capítulo (decisión del Área D: CFI exacto "lazy al pulsar"). Esta
 * función abre el EPUB con JSZip, parsea el XHTML del capítulo, localiza el
 * `snippet` (texto del chunk) en los nodos de texto y construye un CFI
 * apuntando al inicio del snippet usando el módulo puro `foliate-js/epubcfi`
 * (`fake.fromIndex` para la parte del spine + `fromRange` para la posición).
 *
 * Si el snippet no se encuentra exactamente (p. ej. por diferencias de
 * espacios al colapsar), cae al CFI de inicio de capítulo.
 */

import * as CFI from 'foliate-js/epubcfi.js'
import { openZip } from '@/epub/parser'

export interface ChunkAnchor {
  /** href del XHTML del capítulo, relativo a la raíz del zip. */
  chapterHref: string
  /** Índice ordinal del capítulo en el spine. */
  spineIndex: number
  /** Texto del chunk a localizar dentro del capítulo. */
  snippet: string
}

const domParser = new DOMParser()

/**
 * Resuelve el CFI de inicio de un chunk dentro de un EPUB.
 *
 * @param epubBlob el archivo EPUB completo (su `fileBlob`).
 * @param anchor `{ chapterHref, spineIndex, snippet }`.
 * @returns CFI string, o `null` si no se pudo resolver nada.
 */
export async function resolveChunkCfi(
  epubBlob: Blob,
  anchor: ChunkAnchor,
): Promise<string | null> {
  const base = CFI.fake.fromIndex(anchor.spineIndex)

  try {
    const zip = await openZip(epubBlob)
    const entry = zip.file(anchor.chapterHref)
    if (!entry) return base
    const xhtml = await entry.async('text')
    const doc = domParser.parseFromString(xhtml, 'application/xhtml+xml')
    if (doc.getElementsByTagName('parsererror').length > 0) return base

    const range = findSnippetRange(doc, anchor.snippet)
    if (!range) return base
    const rangePart = CFI.fromRange(range)
    return CFI.joinIndir(base, rangePart)
  } catch {
    return base
  }
}

/**
 * Busca el `snippet` (texto colapsado del chunk) dentro de los nodos de
 * texto del documento y devuelve un `Range` colapsado en su inicio.
 *
 * Compara versiones normalizadas (sin importar mayúsculas/acentos) porque
 * el chunk puede haber colapsado espacios de forma ligeramente distinta al
 * DOM original; al localizar la mejor ventana, creamos el range sobre el
 * texto original para que el CFI sea válido en el reader.
 */
function findSnippetRange(doc: Document, snippet: string): Range | null {
  const target = normalize(snippet)
  if (!target) return null
  // Tomamos un prefijo estable del snippet para buscar (los chunks pueden
  // ser largos; los primeros ~60 chars suelen ser suficientes y únicos
  // dentro de un capítulo).
  const probe = target.slice(0, 60)

  const walker = doc.createTreeWalker(
    doc.body ?? doc.documentElement,
    NodeFilter.SHOW_TEXT,
  )
  let node: Text | null
  // Acumulamos texto con un mapa offset→nodo para reconstruir el rango.
  const segments: { node: Text; start: number; length: number }[] = []
  let fullText = ''
  while ((node = walker.nextNode() as Text | null)) {
    const value = node.nodeValue ?? ''
    if (!value) continue
    segments.push({ node, start: fullText.length, length: value.length })
    fullText += value
  }
  const normalized = normalize(fullText)
  // Buscar el probe en el texto normalizado.
  const idx = normalized.toLowerCase().indexOf(probe.toLowerCase())
  if (idx < 0) return null

  // Mapear el offset normalizado de vuelta al texto original. Como la
  // normalización puede cambiar longitudes (acentos → letra base), hacemos
  // una aproximación robusta: localizamos el nodo que contiene el offset
  // usando la longitud original de cada segmento proporcionalmente.
  // Para CFI de inicio basta con apuntar al primer nodo relevante.
  const approxOrigOffset = mapNormalizedToOriginal(fullText, normalized, idx)
  const seg = segmentContaining(segments, approxOrigOffset)
  if (!seg) return null
  const offsetInNode = Math.min(approxOrigOffset - seg.start, seg.length)
  const range = doc.createRange()
  range.setStart(seg.node, Math.max(0, offsetInNode))
  range.collapse(true)
  return range
}

/** Normaliza texto para comparación tolerante (minúsculas, sin acentos). */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Convierte un offset en el texto normalizado a uno aproximado en el texto
 * original. Como la normalización NFD contrae acentos, las longitudes pueden
 * diferir; usamos una proporción simple que es suficiente para apuntar al
 * nodo/carácter correcto en la práctica.
 */
function mapNormalizedToOriginal(original: string, normalized: string, normOffset: number): number {
  if (normalized.length === 0) return 0
  return Math.round((normOffset / normalized.length) * original.length)
}

/** Devuelve el segmento (nodo de texto) que contiene el offset dado. */
function segmentContaining(
  segments: { node: Text; start: number; length: number }[],
  offset: number,
): { node: Text; start: number; length: number } | null {
  for (const seg of segments) {
    if (offset >= seg.start && offset < seg.start + seg.length) return seg
  }
  // Si cae justo al final, último segmento.
  return segments[segments.length - 1] ?? null
}
